import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Users as UsersIcon, Award, MessageSquare, Send } from 'lucide-react';
import { useAuth, formatError } from '../contexts/AuthContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function initials(name) {
  if (!name) return 'U';
  return name.split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();
}

const BADGE_LABELS = {
  l6e_uzman: 'L6e Uzmanı',
  l7e_uzman: 'L7e Uzmanı',
  aktif_uye: 'Aktif Üye',
  ilk_konu: 'İlk Konu',
  ilk_yorum: 'İlk Yorum',
  on_yorum: '10 Yorum',
  etkinlik_katilimci: 'Etkinlik Katılımcısı',
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [sort, setSort] = useState('active');
  const [loading, setLoading] = useState(true);
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [sending, setSending] = useState('');

  const startDM = async (targetId) => {
    if (!me) { navigate('/giris'); return; }
    if (targetId === me._id) return;
    setSending(targetId);
    try {
      const r = await axios.post(
        `${API}/messages/conversations`,
        { participant_ids: [targetId] },
        { withCredentials: true }
      );
      navigate(`/mesajlar/${r.data.conversation._id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSending('');
    }
  };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/users?limit=48&sort=${sort}`)
      .then(r => setUsers(r.data.users || []))
      .finally(() => setLoading(false));
  }, [sort]);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Kullanıcılar · Topluluk Üyeleri"
        description="MiniOto topluluğunun aktif üyeleri, rozetleri ve profilleri. L6e ve L7e topluluğunun bir parçası ol."
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs items={[{ name: 'Kullanıcılar' }]} />
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <UsersIcon className="w-8 h-8 text-emerald-600" />
              Kullanıcılar
            </h1>
            <p className="text-slate-500 mt-2">Topluluk üyelerini keşfet</p>
          </div>
          <div className="inline-flex items-center bg-white rounded-full p-1 border border-slate-100 shadow-sm">
            {[
              { k: 'active', l: 'Aktif' },
              { k: 'badges', l: 'Rozetli' },
              { k: 'new', l: 'Yeni' },
            ].map(opt => (
              <button key={opt.k} onClick={() => setSort(opt.k)}
                data-testid={`users-sort-${opt.k}`}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition ${
                  sort === opt.k ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:text-slate-900'
                }`}>
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">Yükleniyor...</div>
        ) : users.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
            <UsersIcon className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500">Henüz kullanıcı yok.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {users.map(u => (
              <div key={u._id} data-testid={`user-card-${u._id}`}
                className="bg-white rounded-2xl border border-slate-100 p-5 text-center hover:shadow-md transition-all flex flex-col">
                <Link to={`/profil/${u._id}`} className="block flex-1" data-testid={`user-card-link-${u._id}`}>
                  <Avatar className="w-16 h-16 mx-auto mb-3">
                    <AvatarImage src={u.avatar_url ? (u.avatar_url.startsWith('http') ? u.avatar_url : `${API}/files/${u.avatar_url}`) : ''} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-700 font-bold">{initials(u.display_name || u.username)}</AvatarFallback>
                  </Avatar>
                  <h3 className="font-bold text-slate-800 truncate">{u.display_name || u.username}</h3>
                  <p className="text-xs text-slate-400 truncate">@{u.username}</p>
                  {u.bio && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{u.bio}</p>}

                  <div className="flex justify-center gap-4 mt-3 text-xs text-slate-600">
                    <span className="flex items-center gap-1">
                      <MessageSquare className="w-3 h-3 text-emerald-600" />
                      {u.topic_count || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <Award className="w-3 h-3 text-amber-500" />
                      {(u.badge_keys || []).length}
                    </span>
                  </div>

                  {(u.badge_keys || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 justify-center mt-3">
                      {u.badge_keys.slice(0, 2).map(bk => (
                        <span key={bk} className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">
                          {BADGE_LABELS[bk] || bk}
                        </span>
                      ))}
                    </div>
                  )}
                </Link>

                {me && me._id !== u._id && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => startDM(u._id)}
                    disabled={sending === u._id}
                    className="mt-4 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    data-testid={`user-dm-btn-${u._id}`}
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    {sending === u._id ? 'Açılıyor...' : 'Mesaj Gönder'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
